import { useCallback, useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import useLocationStore from 'shared/state/location';
import { Coordinate } from 'shared/@types';
import { useLocale } from 'shared/library/i18n/use-locale';
import { useUserStore } from 'shared/state/user';
import {
  requestApproximateLocation,
  requestCurrentLocation,
  requestLocationPlace,
  resolveUserLocation,
} from './location-service';

export const useLocation = ({ approximate = false }: { approximate?: boolean } = {}) => {
  const { t } = useTranslation('library');
  const { languageCode } = useLocale();
  const mounted = useRef(true);
  useEffect(() => {
    mounted.current = true;
    return () => {
      mounted.current = false;
    };
  }, []);
  const user = useUserStore((state) => state.user);
  const {
    loading,
    location: deviceLocation,
    approximateLocation,
    approximateLocationReady,
    errorMessage,
    place,
    revision,
  } = useLocationStore();
  const location = resolveUserLocation(deviceLocation, user, approximateLocation);
  const hasKnownLocation =
    deviceLocation !== null || resolveUserLocation(null, user, null) !== null;

  useEffect(() => {
    if (approximate && !hasKnownLocation && !approximateLocationReady) {
      void requestApproximateLocation();
    }
  }, [approximate, hasKnownLocation, approximateLocationReady, revision]);

  const fetchPlace = useCallback(
    (coords: Coordinate) => requestLocationPlace(coords, languageCode),
    [languageCode],
  );

  const getCurrentLocation = useCallback(async () => {
    try {
      const position = await requestCurrentLocation();
      if (!mounted.current) return null;
      const coordinate = { latitude: position.latitude, longitude: position.longitude };
      void fetchPlace(coordinate);
      return coordinate;
    } catch {
      return null;
    }
  }, [fetchPlace]);

  return {
    getCurrentLocation,
    fetchPlace,
    loading,
    location,
    ready: hasKnownLocation || approximateLocationReady,
    // Browsers and Expo report raw English errors; show a translated reason instead.
    errorMessage: errorMessage
      ? t(
          /denied/i.test(errorMessage)
            ? 'use_location.permission_denied'
            : 'use_location.unavailable',
        )
      : null,
    place: deviceLocation ? place : null,
    hasLocationAndPlace: deviceLocation !== null && place !== null && !loading,
  };
};
